/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Step 2: Implementing Hightouch Events SDKs", description = "How to switch to Hightouch SDKs, including a wrapper function that sends events to both tools so you can validate during the migration.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Step 2: Implementing Hightouch Events SDKs",
        description: "How to switch to Hightouch SDKs, including a wrapper function that sends events to both tools so you can validate during the migration.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Install the SDKs",
          "depth": 0,
          "id": "install-the-sdks"
        }, {
          "value": "Implement standard tracking calls (track, page, and identify)",
          "depth": 0,
          "id": "implement-standard-tracking-calls-track-page-and-identify"
        }, {
          "value": "Handle identify and page view events",
          "depth": 0,
          "id": "handle-identify-and-page-view-events"
        }, {
          "value": "Migrate server-side events",
          "depth": 0,
          "id": "migrate-server-side-events"
        }, {
          "value": "Update analytics code across your repositories",
          "depth": 0,
          "id": "update-analytics-code-across-your-repositories"
        }, {
          "value": "Testing the implementation",
          "depth": 0,
          "id": "testing-the-implementation"
        }, {
          "value": "Anonymous IDs and automatically collected information",
          "depth": 0,
          "id": "anonymous-ids-and-automatically-collected-information"
        }, {
          "value": "Optional: Gradual rollout",
          "depth": 0,
          "id": "optional-gradual-rollout"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      code: "code",
      pre: "pre",
      span: "span",
      ul: "ul",
      li: "li",
      strong: "strong",
      ol: "ol"
    }, _provideComponents(), props.components), {StreamingCutoverWarning} = _components;
    if (!StreamingCutoverWarning) _missingMdxReference("StreamingCutoverWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch Events provides SDKs across web, mobile, and server-side languages. This step covers switching from your current event tracking SDK (such as Segment's or RudderStack's) to Hightouch Events."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Events is backward compatible with common tracking APIs like those of Segment and RudderStack, so the initial code changes are usually straightforward. If you're coming from a platform with a different SDK shape, such as Amplitude or mParticle, expect more re-instrumentation — see its platform guide for what differs."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "To compare data side by side during the migration, implement a wrapper\nfunction that sends each event to both your current platform and Hightouch.\nYou'll see this in practice in the JavaScript example below."
        })
      }), "\n", _jsx(_components.h2, {
        id: "install-the-sdks",
        children: "Install the SDKs"
      }), "\n", _jsxs(_components.p, {
        children: ["Install the Hightouch SDK for each source you're migrating. Installation is the same as for a fresh implementation — see ", _jsx(_components.a, {
          href: "/events/sources/overview",
          children: "Connect event sources"
        }), " and the SDK reference for your platform, such as ", _jsx(_components.a, {
          href: "/events/sdks/browser#installation",
          children: "Browser"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/nodejs",
          children: "Node.js"
        }), ", or ", _jsx(_components.a, {
          href: "/events/sdks/python",
          children: "Python"
        }), ". Use the write key from your Hightouch event source."]
      }), "\n", _jsx(_components.p, {
        children: "Because the SDKs accept the same calls as your current tool, the migration-specific work isn't the install — it's sending each call to both tools so you can compare data. That's what the rest of this step covers."
      }), "\n", _jsx(_components.h2, {
        id: "implement-standard-tracking-calls-track-page-and-identify",
        children: "Implement standard tracking calls (track, page, and identify)"
      }), "\n", _jsx(_components.p, {
        children: "During this step, you'll update your event method calls to also call the matching Hightouch SDK method."
      }), "\n", _jsxs(_components.p, {
        children: ["The example below shows a wrapper function that calls the ", _jsx(_components.code, {
          children: "track"
        }), " method of your prior tool and Hightouch Events. Both calls take the same parameters and include a ", _jsx(_components.code, {
          children: "migrationId"
        }), " that lets you validate data during the migration."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "eventName, properties"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " context = { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "migrationId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "getMigrationId"
          }), "() };\n  analytics.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(eventName, properties, context);\n  htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track"
          }), "(eventName, properties, context);\n}\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "getMigrationId"
          }), "(", _jsx(_components.span, {
            className: "hljs-params"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " timestamp = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Date"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "now"
          }), "();\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " randomness = ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Math"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "floor"
          }), "(", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Math"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "random"
          }), "() * ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1000"
          }), ");\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["`", _jsx(_components.span, {
              className: "hljs-subst",
              children: "${timestamp}"
            }), "-", _jsx(_components.span, {
              className: "hljs-subst",
              children: "${randomness}"
            }), "`"]
          }), ";\n}\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This wrapper function does the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Generates a unique ", _jsx(_components.code, {
            children: "migrationId"
          }), " for each event"]
        }), "\n", _jsx(_components.li, {
          children: "Sends the event to your current tracking system"
        }), "\n", _jsx(_components.li, {
          children: "Sends the same event to Hightouch Events"
        }), "\n", _jsxs(_components.li, {
          children: ["Includes the ", _jsx(_components.code, {
            children: "migrationId"
          }), " with both events for later comparison and deduplication"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Attach the ", _jsx(_components.code, {
          children: "migrationId"
        }), " in the event's ", _jsx(_components.code, {
          children: "context"
        }), ", as the examples here do, so it lands consistently across event types. In the warehouse, Hightouch flattens ", _jsx(_components.code, {
          children: "context"
        }), " fields to ", _jsx(_components.code, {
          children: "context_<field>"
        }), " columns and snake-cases the key, so ", _jsx(_components.code, {
          children: "migrationId"
        }), " becomes the ", _jsx(_components.strong, {
          children: _jsx(_components.code, {
            children: "context_migration_id"
          })
        }), " column. The validation and unification queries in later steps use ", _jsx(_components.code, {
          children: "context_migration_id"
        }), " for this reason. (If your event storage destination isn't configured to explode ", _jsx(_components.code, {
          children: "context"
        }), " into columns, the value stays nested under the ", _jsx(_components.code, {
          children: "context"
        }), " JSON instead.)"]
      }), "\n", _jsx(_components.h2, {
        id: "handle-identify-and-page-view-events",
        children: "Handle identify and page view events"
      }), "\n", _jsxs(_components.p, {
        children: ["Create similar wrapper functions for other common event types based on their parameters and type signatures. For example, for an ", _jsx(_components.code, {
          children: "identify"
        }), " method in the Browser SDK,"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-jsx",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identifyUser"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "userId, traits"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " context = { ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "migrationId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "getMigrationId"
          }), "() };\n\n  analytics.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(userId, traits, context);\n  htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "(userId, traits, context);\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "migrate-server-side-events",
        children: "Migrate server-side events"
      }), "\n", _jsxs(_components.p, {
        children: ["Follow a similar pattern for server-side event tracking. Below, we show an example of using the ", _jsx(_components.a, {
          href: "/events/sdks/python",
          children: "Python SDK"
        }), " with a wrapper function."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-python",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " segment.analytics ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " analytics\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " hightouch.htevents ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " htevents\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " uuid\n\nanalytics.write_key(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'YOUR_SEGMENT_WRITE_KEY'"
          }), ")\nhtevents.write_key(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'YOUR_HIGHTOUCH_WRITE_KEY'"
          }), ")\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "def"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "track_event"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "user_id, event_name, properties"
          }), "):\n    context = {", _jsx(_components.span, {
            className: "hljs-string",
            children: "'migrationId'"
          }), ": ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "str"
          }), "(uuid.uuid4())}\n\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Track with Segment"
          }), "\n    analytics.track(user_id, event_name, properties, context=context)\n\n    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Track with Hightouch"
          }), "\n    htevents.track(user_id, event_name, properties, context=context)\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "update-analytics-code-across-your-repositories",
        children: "Update analytics code across your repositories"
      }), "\n", _jsxs(_components.p, {
        children: ["If your codebase historically directly calls Segment's (or another provider's) library to trigger events, use your preferred method for making bulk code changes across your repository or repositories, whether it's using ", _jsx(_components.code, {
          children: "grep"
        }), " / ", _jsx(_components.code, {
          children: "find"
        }), " and ", _jsx(_components.code, {
          children: "sed"
        }), ", find and replace across a workspace in your editor, or other tools to replace track, identify, group, page, and screen calls with the wrapper functions."]
      }), "\n", _jsx(_components.p, {
        children: "Make sure that you don't just update the analytics code with the wrapper function(s), but also update library imports across files that call the new wrapper function."
      }), "\n", _jsx(_components.p, {
        children: "If you've already implemented wrapper functions and have modified them using the preceding migration examples, you should be all set."
      }), "\n", _jsx(_components.h2, {
        id: "testing-the-implementation",
        children: "Testing the implementation"
      }), "\n", _jsx(_components.p, {
        children: "After implementing the wrapper functions:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Test thoroughly in a development or staging environment."
        }), "\n", _jsx(_components.li, {
          children: "Verify that events are being received by both your current system and Hightouch Events."
        }), "\n", _jsxs(_components.li, {
          children: ["Check that the ", _jsx(_components.code, {
            children: "migrationId"
          }), " is correctly included in events from both systems."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "anonymous-ids-and-automatically-collected-information",
        children: "Anonymous IDs and automatically collected information"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch Events’ Browser, iOS, and Android SDKs automatically collect a range of information. Read ", _jsx(_components.a, {
          href: "/events/event-spec#automatically-captured-fields",
          children: "the docs"
        }), " for the full list of fields."]
      }), "\n", _jsxs(_components.p, {
        children: ["Anonymous-ID continuity depends on the SDK. On ", _jsx(_components.strong, {
          children: "iOS"
        }), ", Hightouch Events reuses the anonymous ID that Segment or RudderStack already assigned, so returning users keep their session instead of starting fresh. On ", _jsx(_components.strong, {
          children: "Android"
        }), ", the SDK stores the anonymous ID under its own keys and doesn't read the previous provider's value, so those users get a new anonymous ID at cutover. Rely on ", _jsx(_components.code, {
          children: "userId"
        }), " continuity to connect known users across the switch on every platform."]
      }), "\n", _jsx(_components.h2, {
        id: "optional-gradual-rollout",
        children: "Optional: Gradual rollout"
      }), "\n", _jsx(_components.p, {
        children: "Depending on the number of your event sources, your event volume, and whether any of them power critical downstream services, you can consider implementing a gradual rollout strategy. This can help build the migration team's confidence in Hightouch Events and make the migration process more manageable by constraining event volume and sources in the first migration efforts."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start using Hightouch Events with a small percentage of your user base or a specific segment. Some organizations migrate a portion of traffic to start with, while others migrate sources related to a particular platform (such as mobile apps) before migrating additional platforms."
        }), "\n", _jsx(_components.li, {
          children: "Monitor for any issues or discrepancies."
        }), "\n", _jsx(_components.li, {
          children: "Gradually increase the percentage of users or sources on the new system."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "By following these steps, you'll have successfully implemented the Hightouch SDK alongside your existing event tracking system. This dual-tracking approach allows for thorough validation and comparison in the next phases of the migration process."
      }), "\n", _jsx(StreamingCutoverWarning, {}), "\n", _jsxs(_components.p, {
        children: ["As you instrument, set up ", _jsx(_components.a, {
          href: "/events/contracts/management",
          children: "data contracts"
        }), " for your core events to catch bad data before it reaches your warehouse. Version your contract events so older app versions keep validating during the rollout, and start permissive — allow violations with a warning, then tighten as your data stabilizes."]
      }), "\n", _jsxs(_components.p, {
        children: ["In the next step, you'll ", _jsx(_components.a, {
          href: "validating-your-migration",
          children: "validate your migration"
        }), " to confirm that Hightouch Events captures data correctly and consistently with your previous system."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
